import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { MapPin, Phone, Mail, Clock, CheckCircle2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const DEFAULT_CONTACT = {
  address_lines: ['Via Cassia, Loc. Cipressino SNC', '50028 Barberino Tavarnelle (FI)', 'Italia'],
  phones: ['+39 055 5180105', '+39 339 2020405'],
  emails: ['info@bettisrl.net'],
  hours_lines: ['Lun – Ven: 9:00 – 18:00', 'Sab: 9:00 – 13:00 (su appuntamento)', 'Domenica: su appuntamento'],
  intro: 'Siamo a tua disposizione per qualsiasi richiesta tecnica, preventivo o consulenza sulle attrezzature audio, luci e video.',
};

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [contact, setContact] = useState(DEFAULT_CONTACT);

  useEffect(() => {
    api.get('/site-info')
      .then((r) => setContact(r.data.contact || DEFAULT_CONTACT))
      .catch(() => setContact(DEFAULT_CONTACT));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/contacts', form);
      setSuccess(true);
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
      toast({ title: 'Messaggio inviato', description: 'Grazie per averci contattato!' });
    } catch {
      toast({ title: 'Errore', description: 'Impossibile inviare il messaggio.', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-6 py-10 fade-in" data-testid="contact-page">
      <h1 className="text-4xl font-black brand-text text-neutral-900">Contatti</h1>
      {contact.intro && (
        <p className="text-neutral-600 mt-2 max-w-2xl" data-testid="contact-intro">{contact.intro}</p>
      )}

      <div className="mt-10 grid lg:grid-cols-2 gap-10">
        <div className="space-y-6">
          <InfoBlock icon={<MapPin size={20} />} title="Indirizzo" lines={contact.address_lines} testid="contact-address" />
          <InfoBlock icon={<Phone size={20} />} title="Telefono" lines={contact.phones} testid="contact-phones" />
          <InfoBlock icon={<Mail size={20} />} title="Email" lines={contact.emails} testid="contact-emails" />
          <InfoBlock icon={<Clock size={20} />} title="Orario" lines={contact.hours_lines} testid="contact-hours" />
        </div>

        <form onSubmit={submit} className="bg-neutral-50 rounded-xl border border-neutral-200 p-6 md:p-8">
          <h2 className="text-2xl font-black brand-text text-neutral-900 mb-4">Scrivici</h2>
          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded text-green-800 text-sm flex items-center gap-2">
              <CheckCircle2 size={16} /> Messaggio inviato con successo!
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Nome *" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
            <Field label="Email *" type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
            <Field label="Telefono" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
            <Field label="Oggetto" value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} />
          </div>
          <div className="mt-4">
            <label className="block text-sm font-bold text-neutral-700 mb-1">Messaggio *</label>
            <textarea
              required
              rows={5}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="w-full px-4 py-2.5 border border-neutral-300 rounded focus:outline-none focus:ring-2 focus:ring-[#d62828]/30 focus:border-[#d62828]"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="mt-4 w-full md:w-auto px-8 py-3 bg-[#d62828] text-white rounded font-bold hover:bg-[#b41f1f] transition-colors disabled:opacity-60"
          >
            {submitting ? 'Invio...' : 'Invia Messaggio'}
          </button>
        </form>
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, type = 'text', required }) => (
  <div>
    <label className="block text-sm font-bold text-neutral-700 mb-1">{label}</label>
    <input
      type={type}
      required={required}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-4 py-2.5 border border-neutral-300 rounded focus:outline-none focus:ring-2 focus:ring-[#d62828]/30 focus:border-[#d62828]"
    />
  </div>
);

const InfoBlock = ({ icon, title, lines, testid }) => (
  <div className="flex gap-4" data-testid={testid}>
    <div className="w-12 h-12 rounded-full bg-[#d62828]/10 text-[#d62828] flex items-center justify-center flex-shrink-0">
      {icon}
    </div>
    <div>
      <h3 className="font-bold text-neutral-800 mb-1">{title}</h3>
      {(lines || []).filter(Boolean).map((l, i) => <p key={`line-${i}-${l.slice(0,20)}`} className="text-neutral-600 text-sm">{l}</p>)}
    </div>
  </div>
);

export default Contact;
